import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { ServiceAddressCard } from "@/services/backend/addresses/components/service-address-card";
import {
  fieldErrors,
  fieldErrorsForPrefix,
  hasFieldError,
  isValidationError,
  rtkErrIsValidationError,
} from "@/shared/lib/errors/validation-error";
import { Label } from "@/shared/components/ui/label";
import { DatePicker } from "@/shared/components/date-picker";
import {
  updateAddress,
  updateBeginDate,
  updateInternalComment,
  updateContact,
  updateEndDate,
  updateExecutingBranch,
  updateRepresentative,
  updateTeamLeader,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";
import { InputValidationErrors } from "@/shared/components/errors/input-error-messages";
import { Address } from "@/services/backend/addresses/types/address";
import { ContactCard } from "@/services/backend/contacts/components/contact-card";
import { Contact } from "@/services/backend/contacts/types/contact";
import { Representative } from "@/services/backend/representatives/types/representative";
import { RepresentativeCard } from "@/services/backend/representatives/components/representative-card";
import { useState } from "react";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { usePutWorkOrder } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-work-order-offline";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useListBranchQuery } from "@/services/backend/branch/api/branch";
import { TextInput } from "@/shared/components/form/text-input";
import { TextareaInput } from "@/shared/components/form/textarea-input";
import { ConnectDocumentDataCard } from "@/services/backend/connect/components/connect-document-data-card";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import {
  OfflineState,
  useOfflineContext,
} from "../../_components/offline-context";

export function WorkOrderData({ workOrder }: { workOrder: WorkOrder }) {
  const { offlineState } = useOfflineContext();

  return (
    <div className="space-y-2">
      <BaseDataCard workOrder={workOrder} />
      <ConnectDocumentDataCard
        serviceKind={GSMServiceKind.HTZ}
        documentKind={GSMDocumentKind.WorkOrder}
        documentId={workOrder.id}
        disabled={offlineState !== OfflineState.ONLINE}
      />
      <AddressCard workOrder={workOrder} />
      <div className="grid gap-2 md:grid-cols-2">
        <ServiceContactCard workOrder={workOrder} />
        <ServiceRepresentativeCard workOrder={workOrder} />
      </div>
    </div>
  );
}

function BaseDataCard({ workOrder }: { workOrder: WorkOrder }) {
  const [request, setRequest] = useState<WorkOrder>(workOrder);
  const mutation = usePutWorkOrder();

  const { error } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    workOrder,
    { resetOnError: false, toastError: false },
  );

  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Arbeitsschein")}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4">
        <ExecutingBranchSelect
          workOrder={request}
          onWorkOrderChange={(wo) => {
            resetNotReadyErrorField("executingBranchId");
            setRequest(wo);
          }}
          error={error || notReadyError}
        />
        <div className="grid grid-cols-2 gap-4">
          <BeginDateInput
            workOrder={request}
            onWorkOrderChange={(wo) => {
              resetNotReadyErrorField("beginDate");
              setRequest(wo);
            }}
            error={error || notReadyError}
          />
          <EndDateInput
            workOrder={request}
            onWorkOrderChange={(wo) => {
              resetNotReadyErrorField("endDate");
              setRequest(wo);
            }}
            error={error || notReadyError}
          />
        </div>
        <TeamLeaderInput
          workOrder={request}
          onWorkOrderChange={(wo) => {
            resetNotReadyErrorField("teamLeader");
            setRequest(wo);
          }}
          error={error || notReadyError}
        />
        <InternalCommentInput
          workOrder={request}
          onWorkOrderChange={(wo) => {
            resetNotReadyErrorField("internalComment");
            setRequest(wo);
          }}
          error={error || notReadyError}
        />
      </CardContent>
    </Card>
  );
}

function ExecutingBranchSelect({
  workOrder,
  onWorkOrderChange,
  error,
}: {
  workOrder: WorkOrder;
  onWorkOrderChange: (workOrder: WorkOrder) => void;
  error: unknown;
}) {
  const onBranchIdChange = (branchId: string) => {
    onWorkOrderChange(updateExecutingBranch(workOrder, branchId));
  };

  const { data: list } = useListBranchQuery({
    company: { active: true, values: [workOrder.companyId] },
  });
  const branches = list?.branches ?? [];

  return (
    <div className="grid gap-1.5">
      <Label>{t("Ausführende Niederlassung")}</Label>
      <Select
        value={workOrder.executingBranchId}
        onValueChange={onBranchIdChange}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Auswählen...")} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>{t("Niederlassung")}</SelectLabel>
            {branches.map((branch) => (
              <SelectItem key={branch.id} value={branch.id}>
                {branch.name}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <InputValidationErrors error={error} field="executingBranchId" />
    </div>
  );
}

function AddressCard({ workOrder }: { workOrder: WorkOrder }) {
  const [request, setRequest] = useState<WorkOrder>(workOrder);
  const mutation = usePutWorkOrder();

  useDebouncedMutation(request, setRequest, mutation, workOrder, {
    resetOnError: false,
    toastError: false,
  });

  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();

  const onServiceAddressChange = (serviceAddress: Address) => {
    setRequest(updateAddress(workOrder, serviceAddress));
  };

  return (
    <ServiceAddressCard
      address={request.address}
      onAddressChange={onServiceAddressChange}
      fieldErrors={fieldErrorsForPrefix(notReadyError, "address.")}
      resetFieldError={(field) => resetNotReadyErrorField(`address.${field}`)}
      disabled={workOrder.immutable}
    />
  );
}

interface DateInputProps {
  workOrder: WorkOrder;
  onWorkOrderChange: (workOrder: WorkOrder) => void;
  error: unknown;
}

function BeginDateInput({
  workOrder,
  onWorkOrderChange,
  error,
}: DateInputProps) {
  const onDateChange = (date: string | null) => {
    onWorkOrderChange(updateBeginDate(workOrder, date));
  };

  const formattedDate = workOrder.beginDate
    ? new Date(workOrder.beginDate)
    : undefined;
  const onFormattedDateChange = (d: Date | undefined) => {
    if (!d) {
      onDateChange(null);
      return;
    }

    // We want to add the current time to the date
    const dateTime = new Date();
    dateTime.setUTCFullYear(d.getUTCFullYear(), d.getMonth(), d.getDate());
    onDateChange(dateTime.toISOString());
  };

  const markError =
    (rtkErrIsValidationError(error) &&
      hasFieldError(error.data, "beginDate")) ||
    (isValidationError(error) && hasFieldError(error, "beginDate"));

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Arbeitsbeginn")}</Label>
      <DatePicker
        date={formattedDate}
        onDateChange={onFormattedDateChange}
        markError={markError}
        disabled={workOrder.immutable}
      />
    </div>
  );
}

function EndDateInput({ workOrder, onWorkOrderChange, error }: DateInputProps) {
  const onDateChange = (date: string | null) => {
    onWorkOrderChange(updateEndDate(workOrder, date));
  };

  const formattedDate = workOrder.endDate
    ? new Date(workOrder.endDate)
    : undefined;
  const onFormattedDateChange = (d: Date | undefined) => {
    if (!d) {
      onDateChange(null);
      return;
    }

    // We want to add the current time to the date
    const dateTime = new Date();
    dateTime.setUTCFullYear(d.getUTCFullYear(), d.getMonth(), d.getDate());
    onDateChange(dateTime.toISOString());
  };

  const markError =
    (rtkErrIsValidationError(error) &&
      hasFieldError(error.data, "beginDate")) ||
    (isValidationError(error) && hasFieldError(error, "beginDate"));

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Arbeitsende")}</Label>
      <DatePicker
        date={formattedDate}
        onDateChange={onFormattedDateChange}
        markError={markError}
        disabled={workOrder.immutable}
      />
    </div>
  );
}

interface TeamLeaderInputProps {
  workOrder: WorkOrder;
  onWorkOrderChange: (workOrder: WorkOrder) => void;
  error: unknown;
}

function TeamLeaderInput({
  workOrder,
  onWorkOrderChange,
  error,
}: TeamLeaderInputProps) {
  const onTeamLeaderChange = (teamLeader: string) => {
    onWorkOrderChange(updateTeamLeader(workOrder, teamLeader));
  };

  const errors = [
    ...(rtkErrIsValidationError(error)
      ? (fieldErrors(error.data, "teamLeader") ?? [])
      : []),
    ...(fieldErrors(error, "teamLeader") ?? []),
  ];

  return (
    <TextInput
      label={t("Teamleiter")}
      field="teamLeader"
      text={workOrder.teamLeader}
      onTextChange={onTeamLeaderChange}
      errors={errors}
    />
  );
}

interface InternalCommentInputProps {
  workOrder: WorkOrder;
  onWorkOrderChange: (workOrder: WorkOrder) => void;
  error: unknown;
}

function InternalCommentInput({
  workOrder,
  onWorkOrderChange,
  error,
}: InternalCommentInputProps) {
  const onInternalCommentChange = (internalComment: string) => {
    onWorkOrderChange(updateInternalComment(workOrder, internalComment));
  };

  const errors = [
    ...(rtkErrIsValidationError(error)
      ? (fieldErrors(error.data, "internalComment") ?? [])
      : []),
    ...(fieldErrors(error, "internalComment") ?? []),
  ];

  return (
    <TextareaInput
      label={t("Interner Kommentar")}
      field="internalComment"
      text={workOrder.internalComment}
      onTextChange={onInternalCommentChange}
      errors={errors}
    />
  );
}

function ServiceContactCard({ workOrder }: { workOrder: WorkOrder }) {
  const [request, setRequest] = useState<WorkOrder>(workOrder);
  const mutation = usePutWorkOrder();

  useDebouncedMutation(request, setRequest, mutation, workOrder, {
    resetOnError: false,
    toastError: false,
  });

  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();

  const onContactChange = (contact: Contact) => {
    setRequest(updateContact(workOrder, contact));
  };

  return (
    <ContactCard
      contact={request.contact}
      onContactChange={onContactChange}
      fieldErrors={fieldErrorsForPrefix(notReadyError, "contact.")}
      resetFieldError={(field) => resetNotReadyErrorField(`contact.${field}`)}
      disabled={workOrder.immutable}
    />
  );
}

function ServiceRepresentativeCard({ workOrder }: { workOrder: WorkOrder }) {
  const [request, setRequest] = useState<WorkOrder>(workOrder);
  const mutation = usePutWorkOrder();

  useDebouncedMutation(request, setRequest, mutation, workOrder, {
    resetOnError: false,
    toastError: false,
  });

  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();

  const onRepresentativeChange = (rep: Representative) => {
    setRequest(updateRepresentative(workOrder, rep));
  };

  return (
    <RepresentativeCard
      representative={request.representative}
      onRepresentativeChange={onRepresentativeChange}
      fieldErrors={fieldErrorsForPrefix(notReadyError, "representative.")}
      resetFieldError={(field) =>
        resetNotReadyErrorField(`representative.${field}`)
      }
      disabled={workOrder.immutable}
    />
  );
}
